import React, { useState, useEffect, useRef } from 'react';
import {
  formatScheduledAt,
  expandSlotsToThirtyMinuteChoices,
  formatSlotRangeInTimezone,
  formatTimezoneAbbr,
} from '../../../helpers/timezone_utils';
import { maskPhoneNumber } from '../../../helpers';
import { failToast } from '../../ui_kit/utilities/fail_toast';
import { cancelCall, acceptCall } from '../../../api/v2/calls.js';

import styles from './index.module.css';

const getSecondsRemaining = (expiresAt) => {
  if (!expiresAt) return 0;
  return Math.max(0, Math.floor((new Date(expiresAt) - new Date()) / 1000));
};

const formatCountdown = (seconds) => {
  if (seconds >= 3600) {
    const h = Math.floor(seconds / 3600)
      .toString()
      .padStart(2, '0');
    const m = Math.floor((seconds % 3600) / 60)
      .toString()
      .padStart(2, '0');
    const s = (seconds % 60).toString().padStart(2, '0');
    return `${h}:${m}:${s}`;
  }
  const m = Math.floor(seconds / 60)
    .toString()
    .padStart(2, '0');
  const s = (seconds % 60).toString().padStart(2, '0');
  return `${m}:${s}`;
};

export default function ActiveCallStatus({
  activeCall,
  onCancel,
  onExpire,
  onAccepted,
  onCounter,
  patientTimezone,
  patientPhone,
  mentorFirstName,
}) {
  const isMounted = useRef(true);
  useEffect(
    () => () => {
      isMounted.current = false;
    },
    []
  );

  const [countdown, setCountdown] = useState(getSecondsRemaining(activeCall?.acceptance_expires_at));
  const [confirming, setConfirming] = useState(false);
  const [canceling, setCanceling] = useState(false);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [accepting, setAccepting] = useState(false);
  const [collapsed, setCollapsed] = useState(false);

  const patientTz = patientTimezone || Intl.DateTimeFormat().resolvedOptions().timeZone;
  const patientTzAbbr = formatTimezoneAbbr(patientTz);
  const mentor = mentorFirstName || 'your mentor';

  const handleAccept = () => {
    if (!selectedSlot) return;
    setAccepting(true);
    acceptCall({ callId: activeCall.uuid, scheduledAt: selectedSlot.start_at })
      .then((updatedCall) => {
        onAccepted?.(updatedCall);
      })
      .catch((err) => {
        failToast(err.message || 'Could not confirm. Please try again.');
      })
      .finally(() => {
        if (isMounted.current) setAccepting(false);
      });
  };

  useEffect(() => {
    const expiresAt = activeCall?.acceptance_expires_at;

    if (!expiresAt) {
      setCountdown(0);
      return () => {};
    }

    setCountdown(getSecondsRemaining(expiresAt));
    const interval = setInterval(() => {
      const remaining = getSecondsRemaining(expiresAt);
      setCountdown(remaining);
      if (remaining === 0) clearInterval(interval);
    }, 1000);

    return () => clearInterval(interval);
  }, [activeCall?.acceptance_expires_at]);

  const handleCancel = () => {
    setCanceling(true);
    cancelCall({ callId: activeCall.uuid })
      .then(() => {
        onCancel();
      })
      .catch((err) => {
        failToast(
          (err.message || 'Could not cancel. Please try again.') +
            ' Please try again or reach out to support@patientpartner.com if this continues.'
        );
      })
      .finally(() => {
        if (isMounted.current) setCanceling(false);
      });
  };

  const { status, scheduled_at, scheduled, countered_at, countered_by_type, proposed_slots, accepted_by_type } =
    activeCall || {};

  if (countered_at && countered_by_type === 'PatientPartner' && proposed_slots?.length > 0 && status === 'pending') {
    const slotChoices = expandSlotsToThirtyMinuteChoices(proposed_slots);

    if (confirming) {
      return (
        <div className={styles.wrapper}>
          <div className={styles.title_row}>
            <PhoneIcon />
            <span className={styles.text}>Cancel this call?</span>
          </div>
          <div className={styles.confirm_row}>
            <button className={styles.cancel_btn} onClick={handleCancel} disabled={canceling}>
              {canceling ? 'Canceling...' : 'Yes, cancel'}
            </button>
            <button className={styles.keep_btn} onClick={() => setConfirming(false)}>
              Keep it
            </button>
          </div>
        </div>
      );
    }

    return (
      <div className={styles.wrapper}>
        <div className={styles.header_row} onClick={() => setCollapsed((c) => !c)}>
          <div className={styles.title_row}>
            <PhoneIcon />
            <span className={styles.text}>{mentor} proposed new times — pick one to accept</span>
          </div>
          <CollapseChevron collapsed={collapsed} />
        </div>
        {!collapsed && (
          <>
            <div className={styles.slots}>
              {slotChoices.map((choice) => {
                const isSelected = selectedSlot?.start_at === choice.start_at;
                return (
                  <button
                    key={choice.start_at}
                    className={`${styles.slot_btn} ${isSelected ? styles.slot_selected : ''}`}
                    onClick={() => setSelectedSlot(isSelected ? null : choice)}
                  >
                    {formatSlotRangeInTimezone(
                      choice.start_at,
                      choice.end_at || new Date(new Date(choice.start_at).getTime() + 30 * 60 * 1000).toISOString(),
                      patientTz,
                      patientTzAbbr
                    )}
                  </button>
                );
              })}
            </div>
            <div className={styles.actions}>
              <button className={styles.keep_btn} onClick={handleAccept} disabled={!selectedSlot || accepting}>
                {accepting ? 'Accepting...' : 'Accept selected time'}
              </button>
              <button className={styles.cancel_btn} onClick={() => setConfirming(true)}>
                Cancel
              </button>
              {onCounter && (
                <button className={styles.counter_btn} onClick={onCounter}>
                  Counter with new times
                </button>
              )}
            </div>
            {countdown > 0 && <span className={styles.expires}>Expires in {formatCountdown(countdown)}</span>}
          </>
        )}
      </div>
    );
  }

  if (status === 'declined') {
    return (
      <div className={styles.wrapper}>
        <div className={styles.title_row}>
          <PhoneIcon />
          <span className={styles.text}>Your call request was declined.</span>
        </div>
        <div className={styles.actions}>
          <button className={styles.cancel_btn} onClick={onExpire}>
            Dismiss
          </button>
        </div>
      </div>
    );
  }

  if (status === 'mentor_unreachable') {
    return (
      <div className={styles.wrapper}>
        <div className={styles.title_row}>
          <PhoneIcon />
          <span className={styles.text}>We couldn't reach your mentor. Please try again later.</span>
        </div>
        <div className={styles.actions}>
          <button className={styles.cancel_btn} onClick={onExpire}>
            Dismiss
          </button>
        </div>
      </div>
    );
  }

  if (status === 'patient_unreachable') {
    return (
      <div className={styles.wrapper}>
        <div className={styles.title_row}>
          <PhoneIcon />
          <span className={styles.text}>
            We couldn't reach you for the call. Please ensure your phone is available.
          </span>
        </div>
        <div className={styles.actions}>
          <button className={styles.cancel_btn} onClick={onExpire}>
            Dismiss
          </button>
        </div>
      </div>
    );
  }

  if (status === 'in_progress') {
    return (
      <div className={`${styles.wrapper} ${styles.wrapper_accepted}`}>
        <div className={styles.title_row}>
          <PhoneIcon />
          <span className={styles.text}>Call in progress</span>
        </div>
      </div>
    );
  }

  if (status === 'accepted') {
    const acceptedWrapperClass = `${styles.wrapper} ${styles.wrapper_accepted}`;

    if (confirming) {
      return (
        <div className={acceptedWrapperClass}>
          <div className={styles.title_row}>
            <PhoneIcon />
            <span className={styles.text}>Cancel this call?</span>
          </div>
          <div className={styles.confirm_row}>
            <button className={styles.cancel_btn} onClick={handleCancel} disabled={canceling}>
              {canceling ? 'Canceling...' : 'Yes, cancel'}
            </button>
            <button className={styles.keep_btn} onClick={() => setConfirming(false)}>
              Keep it
            </button>
          </div>
        </div>
      );
    }

    const maskedPhone = maskPhoneNumber(patientPhone);
    const viewerAccepted = accepted_by_type === 'Patient';
    const acceptorLabel = viewerAccepted ? 'You' : mentor;
    const acceptedSummary = scheduled_at
      ? `Scheduled for ${formatScheduledAt(scheduled_at, patientTz)}`
      : maskedPhone
        ? `${acceptorLabel} accepted, we'll call you shortly at ${maskedPhone}`
        : `${acceptorLabel} accepted, we'll call you shortly`;

    return (
      <div className={acceptedWrapperClass}>
        <div className={styles.header_row} onClick={() => setCollapsed((c) => !c)}>
          <div className={styles.title_row}>
            <PhoneIcon />
            <span className={styles.text}>{acceptedSummary}</span>
          </div>
          <CollapseChevron collapsed={collapsed} />
        </div>
        {!collapsed && (
          <>
            {!scheduled_at && !scheduled && (
              <span className={styles.hint}>Keep your phone on. You can leave the app and we&apos;ll ring you.</span>
            )}
            <div className={styles.actions}>
              <button className={styles.cancel_btn} onClick={() => setConfirming(true)}>
                Cancel
              </button>
            </div>
          </>
        )}
      </div>
    );
  }

  const expired = countdown === 0 && !scheduled_at;

  if (expired) {
    return (
      <div className={styles.wrapper}>
        <div className={styles.title_row}>
          <PhoneIcon />
          <span className={styles.text}>Request expired</span>
        </div>
        <div className={styles.actions}>
          <button className={styles.cancel_btn} onClick={onExpire}>
            Dismiss
          </button>
        </div>
      </div>
    );
  }

  if (confirming) {
    return (
      <div className={styles.wrapper}>
        <div className={styles.title_row}>
          <PhoneIcon />
          <span className={styles.text}>Cancel this call?</span>
        </div>
        <div className={styles.confirm_row}>
          <button className={styles.cancel_btn} onClick={handleCancel} disabled={canceling}>
            {canceling ? 'Canceling...' : 'Yes, cancel'}
          </button>
          <button className={styles.keep_btn} onClick={() => setConfirming(false)}>
            Keep it
          </button>
        </div>
      </div>
    );
  }

  const pendingSummary = scheduled_at
    ? `Scheduled for ${formatScheduledAt(scheduled_at, patientTz)}`
    : 'Waiting for your mentor to accept';

  return (
    <div className={styles.wrapper}>
      <div className={styles.header_row} onClick={() => setCollapsed((c) => !c)}>
        <div className={styles.title_row}>
          <PhoneIcon />
          <span className={styles.text}>{pendingSummary}</span>
        </div>
        <CollapseChevron collapsed={collapsed} />
      </div>
      {!collapsed && (
        <>
          {!scheduled_at && !scheduled && <span className={styles.hint}>Make sure your phone is on and available</span>}
          <div className={styles.actions}>
            <button className={styles.cancel_btn} onClick={() => setConfirming(true)}>
              Cancel
            </button>
          </div>
          {!scheduled_at && countdown > 0 && (
            <span className={styles.expires}>Expires in {formatCountdown(countdown)}</span>
          )}
        </>
      )}
    </div>
  );
}

function CollapseChevron({ collapsed }) {
  return (
    <svg
      width='14'
      height='14'
      viewBox='0 0 24 24'
      fill='none'
      xmlns='http://www.w3.org/2000/svg'
      style={{
        flexShrink: 0,
        transition: 'transform 0.2s',
        transform: collapsed ? 'rotate(180deg)' : 'rotate(0deg)',
      }}
    >
      <path d='M18 15L12 9L6 15' stroke='#a07a30' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round' />
    </svg>
  );
}

function PhoneIcon() {
  return (
    <svg width='18' height='18' viewBox='0 0 24 24' fill='none' xmlns='http://www.w3.org/2000/svg'>
      <path
        d='M20.9995 16.4767V19.1864C21.0037 20.2223 20.0723 21.0873 19.0265 20.9929C10.0001 21 3.00006 13.935 3.00713 4.96919C2.91294 3.92895 3.77364 3.00106 4.80817 3.00009H7.52331C7.96253 2.99577 8.38835 3.151 8.72138 3.43684C9.66819 4.24949 10.2772 7.00777 10.0429 8.10428C9.85994 8.96036 8.99696 9.55929 8.41026 10.1448C9.69864 12.4062 11.5747 14.2785 13.8405 15.5644C14.4272 14.9788 15.0274 14.1176 15.8851 13.935C16.9855 13.7008 19.7615 14.3106 20.5709 15.264C20.858 15.6021 21.0105 16.0337 20.9995 16.4767Z'
        stroke='#7a5c00'
        strokeWidth='1.5'
        strokeLinecap='round'
        strokeLinejoin='round'
      />
    </svg>
  );
}
